import { useMutation, UseMutationResult, useQuery, UseQueryResult } from "@tanstack/react-query"
import { CreateDeliveryInput, CreateParkingInput, CreateParkingResponse, DeleteDeliveryInput, DeleteParkingInput, DeliveryPointResponse, GetDeliveryInput, GetOrderDetailResponse, GetParkingByIdResponse, GetParkingListResponse, GetParkingTransportAreaTypeInput, GetParkingTransportDistrictTypeInput, GetParkingTransportProvinceTypeInput, GetParkingTransportTypeInput, GetPrepareShipInput, GetPrepareShipResponse, GetTransportOrderDetailInput, GetTransportShipedResponse, GetTransportShippedDetailInput, GetTransportShippedDetailResponse, ParkingTransportAreaTypeResponse, ParkingTransportDistrictTypeResponse, ParkingTransportProvinceTypeResponse, ParkingTransportTypeResponse, UpdateDeliveryInput, UpdateParkingInput } from "services/interface"
import { createDeliveryPoint, createParking, deleteDeliveryPoint, deleteParking, getDeliveryPoint, getParkingById, getParkingList, getParkingTransportAreaType, getParkingTransportDistrictType, getParkingTransportProvinceType, getParkingTransportType, getPrepareShip, getTransportOrderDetail, getTransportShipped, getTransportShippedOrderDetail, updateDeliveryPoint, updateParking } from "services/shipping"
import { getSpeedShippingConfig, SpeedShippingConfigResponse } from "services/apiSpeedShipping"

export const useGetPrepareShip = (
  input: GetPrepareShipInput
): UseQueryResult<GetPrepareShipResponse, Error> => {
  return useQuery<GetPrepareShipResponse, Error>({
    queryKey: [
      'prepareship',
      input.offset,
      input.limit,
      input.status,
      input.sort_by,
      input.search,
    ],
    queryFn: () => getPrepareShip(input),
    // staleTime: 1000 * 60 * 60 * 24,
  })
}

export const useGetTransportOrderDetail = (): UseMutationResult<
  GetOrderDetailResponse,
  Error,
  GetTransportOrderDetailInput,
  unknown
> => {
  return useMutation<GetOrderDetailResponse, Error, GetTransportOrderDetailInput, unknown>({
    mutationFn: (input: GetTransportOrderDetailInput) => getTransportOrderDetail(input),
  })
}

export const useGetTransportShipped = (
  input: GetPrepareShipInput
): UseQueryResult<GetTransportShipedResponse, Error> => {
  return useQuery<GetTransportShipedResponse, Error>({
    queryKey: [
      'transportshipped',
      input.offset,
      input.limit,
      input.status,
      input.sort_by,
      input.search,
    ],
    queryFn: () => getTransportShipped(input),
    // staleTime: 1000 * 60 * 60 * 24,
  })
}

export const useGetTransportShippedDetail = (): UseMutationResult<
  GetTransportShippedDetailResponse,
  Error,
  GetTransportShippedDetailInput,
  unknown
> => {
  return useMutation<GetTransportShippedDetailResponse, Error, GetTransportShippedDetailInput, unknown>({
    mutationFn: (input: GetTransportShippedDetailInput) => getTransportShippedOrderDetail(input),
  })
}

export const useGetParkingList = (): UseMutationResult<
  GetParkingListResponse,
  Error,
  string,
  unknown
> => {
  return useMutation<GetParkingListResponse, Error, string, unknown>({
    mutationFn: (input: string) => getParkingList(input),
  })
}

export const useCreateParking = (): UseMutationResult<
  CreateParkingResponse,
  Error,
  CreateParkingInput,
  unknown
> => {
  return useMutation<CreateParkingResponse, Error, CreateParkingInput, unknown>({
    mutationFn: (input: CreateParkingInput) => createParking(input),
  })
}

export const useUpdateParking = (): UseMutationResult<
  CreateParkingResponse,
  Error,
  UpdateParkingInput,
  unknown
> => {
  return useMutation<CreateParkingResponse, Error, UpdateParkingInput, unknown>({
    mutationFn: (input: UpdateParkingInput) => updateParking(input),
  })
}

export const useDeleteParking = (): UseMutationResult<
  CreateParkingResponse,
  Error,
  DeleteParkingInput,
  unknown
> => {
  return useMutation<CreateParkingResponse, Error, DeleteParkingInput, unknown>({
    mutationFn: (input: DeleteParkingInput) => deleteParking(input),
  })
}

export const useGetParkingById = (): UseMutationResult<
  GetParkingByIdResponse,
  Error,
  string,
  unknown
> => {
  return useMutation<GetParkingByIdResponse, Error, string, unknown>({
    mutationFn: (input: string) => getParkingById(input),
  })
}

//get delivery point
export const useGetDeliveryPoint = (
  input: GetDeliveryInput
): UseQueryResult<DeliveryPointResponse, Error> => {
  return useQuery<DeliveryPointResponse, Error>({
    queryKey: [
      'deliveryPoint',
      input
    ],
    queryFn: () => getDeliveryPoint(input),
    staleTime: 1000 * 60 * 60 * 24,
  })
}

//create delivery point
export const useCreateDeliveryPoint = (): UseMutationResult<
  DeliveryPointResponse,
  Error,
  CreateDeliveryInput,
  unknown
> => {
  return useMutation<DeliveryPointResponse, Error, CreateDeliveryInput, unknown>({
    mutationFn: (input: CreateDeliveryInput) => createDeliveryPoint(input),
  })
}

//delete delivery point
export const useDeleteDeliveryPoint = (): UseMutationResult<
  DeliveryPointResponse,
  Error,
  DeleteDeliveryInput,
  unknown
> => {
  return useMutation<DeliveryPointResponse, Error, DeleteDeliveryInput, unknown>({
    mutationFn: (input: DeleteDeliveryInput) => deleteDeliveryPoint(input),
  })
}

//update delivery point
export const useUpdateDeliveryPoint = (): UseMutationResult<
  DeliveryPointResponse,
  Error,
  UpdateDeliveryInput,
  unknown
> => {
  return useMutation<DeliveryPointResponse, Error, UpdateDeliveryInput, unknown>({
    mutationFn: (input: UpdateDeliveryInput) => updateDeliveryPoint(input),
  })
}

export const useDeliveryPoint = (): UseMutationResult<
  DeliveryPointResponse,
  Error,
  GetDeliveryInput,
  unknown
> => {
  return useMutation<DeliveryPointResponse, Error, GetDeliveryInput, unknown>({
    mutationFn: (input: GetDeliveryInput) => getDeliveryPoint(input),
  })
}

export const useGetParkingTransportProvinceType = (
  input: GetParkingTransportProvinceTypeInput
): UseQueryResult<ParkingTransportProvinceTypeResponse, Error> => {
  return useQuery<ParkingTransportProvinceTypeResponse, Error>({
    queryKey: [
      'parkingTransportProvinceType',
      input
    ],
    queryFn: () => getParkingTransportProvinceType(input),
    staleTime: 1000 * 60 * 60 * 24,
  })
}

  ///parking/transport_type/district
export const useGetParkingTransportDistrictType = (
  input: GetParkingTransportDistrictTypeInput
): UseQueryResult<ParkingTransportDistrictTypeResponse, Error> => {
  return useQuery<ParkingTransportDistrictTypeResponse, Error>({
    queryKey: [
      'parkingTransportDistrictType',
      input
    ],
    queryFn: () => getParkingTransportDistrictType(input),
    staleTime: 1000 * 60 * 60 * 24,
  })
}

///parking/transport_type
export const useGetParkingTransportType = (
  input: GetParkingTransportTypeInput
): UseQueryResult<ParkingTransportTypeResponse, Error> => {
  return useQuery<ParkingTransportTypeResponse, Error>({
    queryKey: [
      'parkingTransportType',
      input
    ],
    queryFn: () => getParkingTransportType(input),
    staleTime: 1000 * 60 * 60 * 24,
  })
}

export const useGetParkingTransportAreaTypeMutation = (): UseMutationResult<
  ParkingTransportAreaTypeResponse,
  Error,
  GetParkingTransportAreaTypeInput,
  unknown
> => {
  return useMutation<ParkingTransportAreaTypeResponse, Error, GetParkingTransportAreaTypeInput, unknown>(
    {
      mutationFn: (input: GetParkingTransportAreaTypeInput) => getParkingTransportAreaType(input),
    }
  )
}

// Get Speed Shipping Config
export const useGetSpeedShippingConfig = (): UseQueryResult<SpeedShippingConfigResponse, Error> => {
  return useQuery<SpeedShippingConfigResponse, Error>({
    queryKey: ['speedShippingConfig'],
    queryFn: () => getSpeedShippingConfig(),
    staleTime: 1000 * 60 * 60, // 1 hour
    retry: 1, // retry once on failure
  })
}
